import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, Wrench, Scale, Users, FileSearch, CheckCircle, ArrowRight, Zap, TrendingUp, Target, DollarSign, GitMerge, Database, FileStack, UserCheck, Clock, FileBarChart, BarChart3, Award } from 'lucide-react';

const coreStats = [
  { value: '500+', label: 'Projects Streamlined', icon: Building2 },
  { value: '99.9%', label: 'Document Accuracy', icon: FileSearch },
  { value: '60%', label: 'Faster Project Delivery', icon: Zap },
  { value: '10K+', label: 'Team Members Connected', icon: Users }
];

const bidAIStats = [
  { value: '45%', label: 'Faster RFP Turnaround', icon: TrendingUp },
  { value: '85%', label: 'Bid Win Rate', icon: Target },
  { value: '$50M+', label: 'Contracts Secured', icon: DollarSign },
  { value: '200+', label: 'RFPs Processed', icon: FileSearch }
];

const mergeAIStats = [
  { value: '10K+', label: 'Documents Unified', icon: GitMerge },
  { value: '75%', label: 'Faster Due Diligence', icon: TrendingUp },
  { value: '99.8%', label: 'Data Accuracy', icon: Database },
  { value: '50+', label: 'M&A Projects Completed', icon: FileStack }
];

const recruitAIStats = [
  { value: '50%', label: 'Faster Hiring Cycles', icon: Clock },
  { value: '92%', label: 'Quality Match Rate', icon: UserCheck },
  { value: '1000+', label: 'Candidates Screened Monthly', icon: Users },
  { value: '85%', label: 'Hiring Manager Satisfaction', icon: Target }
];

const reportBuilderStats = [
  { value: '70%', label: 'Faster Audit Prep', icon: Clock },
  { value: '100%', label: 'Compliance Accuracy', icon: Award },
  { value: '500+', label: 'Reports Generated Monthly', icon: FileBarChart },
  { value: '95%', label: 'Executive Satisfaction', icon: BarChart3 }
];

const coreIndustryApplications = [
  {
    icon: Building2,
    title: 'Rail & Transit Construction Projects',
    description: 'Streamline massive infrastructure projects with centralized document management and real-time collaboration.',
    benefits: [
      'Blueprint version control',
      'Safety manual accessibility',
      'Real-time project updates',
      'Compliance tracking'
    ]
  },
  {
    icon: Wrench,
    title: 'Civil & Structural Engineering Firms',
    description: 'Manage complex engineering documentation with AI-powered search and intelligent categorization.',
    benefits: [
      'Technical drawing management',
      'Engineering calculation storage',
      'Project specification tracking',
      'Team collaboration tools'
    ]
  },
  {
    icon: Scale,
    title: 'Legal Compliance Teams in Infrastructure',
    description: 'Ensure regulatory compliance with audit-ready documentation and automated compliance checks.',
    benefits: [
      'Regulatory document management',
      'Compliance audit trails',
      'Legal requirement tracking',
      'Risk assessment tools'
    ]
  }
];

const bidAIIndustryApplications = [
  {
    icon: Building2,
    title: 'Heavy Civil & Infrastructure Construction',
    description: 'Win more infrastructure contracts with automated bid preparation and intelligent cost analysis.',
    benefits: [
      'Automated proposal generation',
      'Cost risk analysis',
      'Compliance gap identification',
      'Multi-million dollar bid optimization'
    ]
  },
  {
    icon: Zap,
    title: 'Energy & Utilities Project Bidding',
    description: 'Streamline complex energy project bids with AI-powered analysis and automated documentation.',
    benefits: [
      'Technical specification analysis',
      'Risk assessment automation',
      'Regulatory compliance checking',
      'Competitive positioning insights'
    ]
  },
  {
    icon: Wrench,
    title: 'Engineering Consulting Firms',
    description: 'Accelerate proposal development with intelligent document processing and bid optimization.',
    benefits: [
      'Proposal template automation',
      'Client requirement analysis',
      'Technical solution matching',
      'Project timeline optimization'
    ]
  }
];

const mergeAIIndustryApplications = [
  {
    icon: GitMerge,
    title: 'Mergers & Acquisitions (Infrastructure & Energy)',
    description: 'Accelerate M&A processes with intelligent document consolidation and automated due diligence.',
    benefits: [
      'Contract integration automation',
      'Financial statement consolidation',
      'Risk assessment acceleration',
      'Regulatory compliance verification'
    ]
  },
  {
    icon: Building2,
    title: 'Construction & Contract Management',
    description: 'Unify complex project documentation across multiple contracts and stakeholders.',
    benefits: [
      'Multi-contract integration',
      'Stakeholder document sharing',
      'Version control automation',
      'Compliance gap identification'
    ]
  },
  {
    icon: Scale,
    title: 'Banking & Legal Advisory Firms',
    description: 'Streamline document-heavy processes with intelligent categorization and secure collaboration.',
    benefits: [
      'Client document consolidation',
      'Regulatory filing automation',
      'Audit trail management',
      'Cross-jurisdictional compliance'
    ]
  }
];

const recruitAIIndustryApplications = [
  {
    icon: Scale,
    title: 'Law Firms & Compliance Practices',
    description: 'Screen legal professionals efficiently with AI-powered certification and expertise matching.',
    benefits: [
      'Certification verification',
      'Legal expertise assessment',
      'Compliance experience matching',
      'Regulatory knowledge screening'
    ]
  },
  {
    icon: Wrench,
    title: 'Engineering Workforce Staffing Agencies',
    description: 'Match technical talent to complex engineering projects with precision and speed.',
    benefits: [
      'Technical skill assessment',
      'Project experience matching',
      'Certification tracking',
      'Industry specialization filtering'
    ]
  },
  {
    icon: Building2,
    title: 'Construction HR & Operations Teams',
    description: 'Build qualified construction teams with automated screening for safety and skills.',
    benefits: [
      'Safety certification verification',
      'Experience level assessment',
      'Equipment operation skills',
      'Project type specialization'
    ]
  },
  {
    icon: Users,
    title: 'Recruitment Firms',
    description: 'Scale recruiting operations with intelligent candidate matching and automated workflows.',
    benefits: [
      'Multi-client requirement matching',
      'Automated candidate scoring',
      'Interview scheduling optimization',
      'Client satisfaction tracking'
    ]
  }
];

const reportBuilderIndustryApplications = [
  {
    icon: Zap,
    title: 'Energy & Utility Companies',
    description: 'Generate compliance-ready safety audits and performance reports across multiple facilities.',
    benefits: [
      'OSHA compliance reporting',
      'ISO standards alignment',
      'Multi-plant dashboard creation',
      'Real-time performance tracking'
    ]
  },
  {
    icon: Building2,
    title: 'Transportation & Safety Compliance Regulators',
    description: 'Automate regulatory reporting with standardized templates and real-time data integration.',
    benefits: [
      'Transportation safety reports',
      'Regulatory compliance dashboards',
      'Incident tracking systems',
      'Performance benchmarking'
    ]
  },
  {
    icon: Scale,
    title: 'Legal & Regulatory Reporting Firms',
    description: 'Streamline client reporting with automated compliance documentation and audit trails.',
    benefits: [
      'Client compliance reports',
      'Regulatory filing automation',
      'Audit documentation',
      'Cross-jurisdictional reporting'
    ]
  }
];

const UseCasesShowcase = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wide">
              Use Cases
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight"
          >
            One Platform, <span className="text-blue-600">Infinite Industry Applications</span>
          </motion.h2>
        </div>

        {/* Ufficient Core Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20"
        >
          {/* Core Stats
          <div className="mb-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                      <Icon size={32} className="text-blue-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
                    <p className="text-gray-600 font-medium">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div> */}

          <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-3xl p-2 md:p-4 lg:p-8 text-white mb-16">
            <div className="text-center mb-12">
              <span className="text-blue-300 font-semibold text-sm uppercase tracking-wide mb-4 block">
                Ufficient Core
              </span>
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Clarity for <span className="text-blue-300">Complex Projects</span>
              </h3>
              <p className="text-xl text-blue-100 mb-6">Centralized Knowledge & Collaboration</p>
              
              <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10">
                <h4 className="text-xl font-semibold text-white mb-4">Construction & Engineering Projects</h4>
                <p className="text-blue-100 leading-relaxed">
                  A rail and transit construction consortium consolidates project blueprints, safety manuals, and engineering drawings in CoreAI. With AI-powered document search and version control, site managers and engineers access the latest files instantly, eliminating rework and avoiding costly delays.
                </p>
              </div>
            </div>
          </div>

          {/* Core Industry Applications */}
          <div className="mb-12">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Core Industries Optimized</h3>
              <p className="text-lg text-gray-600">Proven solutions across diverse industry verticals</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {coreIndustryApplications.map((industry, index) => {
                const Icon = industry.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200">
                      <Icon size={32} className="text-blue-600" />
                    </div>
                    
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{industry.title}</h4>
                    <p className="text-gray-600 mb-6 leading-relaxed">{industry.description}</p>
                    
                    <div className="space-y-3">
                      {industry.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center space-x-3">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* BidAI Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          {/* BidAI Stats */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {bidAIStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-emerald-100 text-center group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                      <Icon size={32} className="text-emerald-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
                    <p className="text-gray-600 font-medium">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-900 to-teal-900 rounded-3xl p-8 md:p-12 lg:p-16 text-white mb-16">
            <div className="text-center mb-12">
              <span className="text-emerald-300 font-semibold text-sm uppercase tracking-wide mb-4 block">
                Automated Bid Preparation & Analysis
              </span>
              <span className="text-teal-300 font-semibold text-sm uppercase tracking-wide mb-4 block">
                BidAI
              </span>
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Turn RFPs into <span className="text-emerald-300">Revenue</span>
              </h3>
              
              <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10">
                <h4 className="text-xl font-semibold text-white mb-4">Infrastructure Bidding & RFPs</h4>
                <p className="text-emerald-100 leading-relaxed">
                  A bridge and highway contractor reduces RFP turnaround time by 45% using BidAI. The platform auto-generates proposals, highlights compliance gaps, and analyzes cost risks helping teams submit competitive bids that win multi-million-dollar infrastructure contracts.
                </p>
              </div>
            </div>
          </div>

        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Industry Operations?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Discover how Ufficient's complete AI platform can streamline your specific industry workflows and accelerate project delivery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                Schedule Industry Demo
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/solutions">
              <button className="inline-flex items-center px-8 py-4 bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                Explore All Solutions
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UseCasesShowcase; 